/**
 * Authentication utility functions
 */

/**
 * Checks if a JWT token is expired
 * @param token JWT token string
 * @returns boolean - true if expired, false if valid
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;

  try {
    // JWT tokens are in three parts: header.payload.signature
    const payload = token.split(".")[1];
    // Decode the base64 payload
    const decoded = JSON.parse(atob(payload));
    // Check if token is expired - JWT exp is in seconds since epoch
    const expiryTime = decoded.exp * 1000; // Convert to milliseconds

    // Add a buffer of 10 seconds to account for network latency and clock differences
    return Date.now() >= expiryTime - 10000;
  } catch (error) {
    console.error("Error checking token expiration:", error);

    return true; // If we can't parse the token, assume it's expired for safety
  }
};

/**
 * Clear all auth data from localStorage
 */
export const clearAuthData = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("role");
  localStorage.removeItem("clientId");
  localStorage.removeItem("userId");
  localStorage.removeItem("clientName");
};

/**
 * Redirect to login page
 */
export const redirectToLogin = (): void => {
  clearAuthData();
  window.location.href = "/login";
};

/**
 * Check if user is authenticated by verifying token in localStorage
 * @returns boolean - true if authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem("token");

  if (!token) return false;

  return !isTokenExpired(token);
};

/**
 * Get the current authentication token
 * @returns string | null - Token if valid, null if not
 */
export const getAuthToken = (): string | null => {
  const token = localStorage.getItem("token");

  if (!token || isTokenExpired(token)) {
    return null;
  }

  return token;
};
